import { useState } from 'react';
import { useNavigate } from 'react-router';
import clsx from 'clsx';

import { Button, Card, Icon, Switch } from '@lib/ui';
import { Input, Textarea, Select } from '@lib/ui';
import type { Source, ExtractFields, SourceInput, Step } from '../types';
import { defaultSource, SCENARIOS, inferWorkflow, inferComplexity } from '../types';
import { SourcePreview } from '../components/SourcePreview';

export default function ProgressiveWizard() {
  const navigate = useNavigate();
  const [source, setSource] = useState<Source>(defaultSource);
  const [activeScenario, setActiveScenario] = useState<string | null>(null);
  const [useStructuredExtract, setUseStructuredExtract] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const updateSource = (updates: Partial<Source>) => {
    setSource((prev) => ({ ...prev, ...updates }));
  };

  const loadScenario = (scenarioId: string) => {
    const scenario = SCENARIOS.find((s) => s.id === scenarioId);
    if (scenario) {
      setActiveScenario(scenarioId);
      setSource(scenario.source);
      setUseStructuredExtract(typeof scenario.source.extract === 'object');
      setShowAdvanced(scenario.complexity === 'custom');
    }
  };

  const complexity = inferComplexity(source);
  const workflow = inferWorkflow(source);

  // Handle extract fields
  const extractFields = typeof source.extract === 'object' ? source.extract : {};
  const extractText = typeof source.extract === 'string' ? source.extract : '';

  const addExtractField = () => {
    const newFields = { ...extractFields, '': '' };
    updateSource({ extract: newFields });
  };

  const updateExtractField = (oldKey: string, newKey: string, value: string) => {
    const newFields: ExtractFields = {};
    for (const [k, v] of Object.entries(extractFields)) {
      if (k === oldKey) {
        if (newKey) newFields[newKey] = value;
      } else {
        newFields[k] = v;
      }
    }
    updateSource({ extract: newFields });
  };

  const removeExtractField = (key: string) => {
    const newFields = { ...extractFields };
    delete newFields[key];
    updateSource({ extract: newFields });
  };

  // Handle inputs
  const addInput = () => {
    const newInputs: SourceInput[] = [
      ...(source.inputs || []),
      { name: '', type: 'string', required: true },
    ];
    updateSource({ inputs: newInputs });
  };

  const updateInput = (index: number, updates: Partial<SourceInput>) => {
    const newInputs = [...(source.inputs || [])];
    newInputs[index] = { ...newInputs[index], ...updates };
    updateSource({ inputs: newInputs });
  };

  const removeInput = (index: number) => {
    updateSource({ inputs: source.inputs?.filter((_, i) => i !== index) });
  };

  // Handle steps
  const addStep = (type: Step['type']) => {
    let config: Step['config'];
    switch (type) {
      case 'discover':
        config = { site: '', scope: '' };
        break;
      case 'fetch':
        config = '';
        break;
      case 'filter':
        config = '';
        break;
      case 'select':
        config = { strategy: 'random', count: 1 };
        break;
      case 'extract':
        config = {};
        break;
      case 'transform':
        config = '';
        break;
    }
    const newSteps: Step[] = [...(source.steps || []), { type, config } as Step];
    updateSource({ steps: newSteps });
  };

  const removeStep = (index: number) => {
    updateSource({ steps: source.steps?.filter((_, i) => i !== index) });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Header */}
      <div className="bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-600 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 w-10 h-10 rounded-lg flex items-center justify-center">
              <Icon name="folder" className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Source Builder
              </h1>
              <p className="text-sm text-gray-500 dark:text-dark-400">
                Progressive disclosure model
              </p>
            </div>
          </div>
          <Button variant="flat" onClick={() => navigate('/poc/source-modeling')}>
            Back
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Scenario selector */}
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
            Load example scenario:
          </p>
          <div className="flex flex-wrap gap-2">
            {SCENARIOS.map((scenario) => (
              <button
                key={scenario.id}
                onClick={() => loadScenario(scenario.id)}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  activeScenario === scenario.id
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-dark-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                )}
              >
                <span className="mr-1.5">{scenario.name}</span>
                <span className="text-xs opacity-60">({scenario.complexity})</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Form */}
          <div className="space-y-6">
            <Card skin="shadow" className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Basic Configuration
              </h2>

              <div className="space-y-6">
                {/* Title */}
                <Input
                  label="Title"
                  placeholder="e.g., Article Summary, Product Finder"
                  value={source.title}
                  onChange={(e) => updateSource({ title: e.target.value })}
                />

                {/* Description */}
                <Input
                  label="Description (optional)"
                  placeholder="What does this source do?"
                  value={source.description || ''}
                  onChange={(e) => updateSource({ description: e.target.value || undefined })}
                />

                {/* Content Source - URL or Site */}
                <div className="p-4 rounded-lg border border-gray-200 dark:border-dark-600 space-y-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Content Source
                  </p>

                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="sourceType"
                        checked={!!source.url || (!source.url && !source.site)}
                        onChange={() =>
                          updateSource({ url: '', site: undefined, scope: undefined })
                        }
                        className="text-blue-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-dark-200">Single URL</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="sourceType"
                        checked={!!source.site}
                        onChange={() => updateSource({ site: '', scope: '', url: undefined })}
                        className="text-blue-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-dark-200">
                        Discover from Site
                      </span>
                    </label>
                  </div>

                  {(!!source.url || (!source.url && !source.site)) && !source.site && (
                    <Input
                      label="URL"
                      placeholder="https://example.com/article"
                      value={source.url || ''}
                      onChange={(e) => updateSource({ url: e.target.value })}
                    />
                  )}

                  {!!source.site && (
                    <div className="space-y-4">
                      <Input
                        label="Site"
                        placeholder="example.com"
                        value={source.site || ''}
                        onChange={(e) => updateSource({ site: e.target.value })}
                      />
                      <Input
                        label="Scope (path)"
                        placeholder="/c/supplements, /blog, /products"
                        value={source.scope || ''}
                        onChange={(e) => updateSource({ scope: e.target.value })}
                      />
                    </div>
                  )}
                </div>

                {/* Extract */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-fg">What to Extract</label>
                    <Switch
                      checked={useStructuredExtract}
                      onChange={(e) => {
                        setUseStructuredExtract(e.target.checked);
                        if (e.target.checked) {
                          updateSource({ extract: {} });
                        } else {
                          updateSource({ extract: '' });
                        }
                      }}
                      size="sm"
                      label="Structured fields"
                    />
                  </div>

                  {!useStructuredExtract ? (
                    <Textarea
                      placeholder="Describe what to extract: main points, key quotes, product details..."
                      rows={3}
                      value={extractText}
                      onChange={(e) => updateSource({ extract: e.target.value })}
                    />
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(extractFields).map(([key, value], index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            placeholder="field_name"
                            value={key}
                            onChange={(e) => updateExtractField(key, e.target.value, value)}
                            classNames={{ root: 'w-1/3' }}
                          />
                          <Input
                            placeholder="extraction instruction"
                            value={value}
                            onChange={(e) => updateExtractField(key, key, e.target.value)}
                            classNames={{ root: 'flex-1' }}
                          />
                          <Button
                            isIcon
                            variant="flat"
                            color="error"
                            size="sm"
                            onClick={() => removeExtractField(key)}
                          >
                            <Icon name="x" size="sm" />
                          </Button>
                        </div>
                      ))}
                      <Button size="sm" variant="soft" onClick={addExtractField}>
                        <Icon name="plus" size="sm" />
                        <span className="ml-1">Add Field</span>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Optional Modifiers */}
            {source.site && (
              <Card skin="shadow" className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  Optional Modifiers
                </h2>

                <div className="space-y-6">
                  {/* Filter */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-fg">Filter Results</label>
                      <Switch
                        checked={!!source.filter}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateSource({ filter: '' });
                          } else {
                            updateSource({ filter: undefined });
                          }
                        }}
                        size="sm"
                      />
                    </div>
                    {source.filter !== undefined && (
                      <Input
                        placeholder="e.g., 4+ star rating, has positive reviews"
                        value={source.filter}
                        onChange={(e) => updateSource({ filter: e.target.value })}
                        description="LLM will filter results based on this criteria"
                      />
                    )}
                  </div>

                  {/* Select */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-fg">Select from Results</label>
                      <Switch
                        checked={!!source.select}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateSource({ select: 'random' });
                          } else {
                            updateSource({ select: undefined });
                          }
                        }}
                        size="sm"
                      />
                    </div>
                    {source.select !== undefined && (
                      <Select
                        value={
                          typeof source.select === 'string' ? source.select : source.select.strategy
                        }
                        onChange={(e) =>
                          updateSource({ select: e.target.value as 'random' | 'first' | 'best' })
                        }
                        data={[
                          { value: 'random', label: 'Random' },
                          { value: 'first', label: 'First match' },
                          { value: 'best', label: 'Best match' },
                        ]}
                        description="How to select from filtered results"
                      />
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Advanced Mode */}
            <Card skin="shadow" className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Advanced Mode
                </h2>
                <Switch
                  checked={showAdvanced}
                  onChange={(e) => {
                    setShowAdvanced(e.target.checked);
                    if (!e.target.checked) {
                      updateSource({ steps: undefined, inputs: undefined });
                    }
                  }}
                  size="sm"
                  label="Custom workflow"
                />
              </div>

              {showAdvanced && (
                <div className="space-y-6">
                  {/* Inputs */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-medium text-fg">Runtime Inputs</label>
                      <Button size="sm" variant="soft" onClick={addInput}>
                        <Icon name="plus" size="sm" />
                        <span className="ml-1">Add Input</span>
                      </Button>
                    </div>
                    {!source.inputs || source.inputs.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-dark-400 italic">
                        No runtime inputs. Add inputs for dynamic values.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {source.inputs.map((input, index) => (
                          <div key={index} className="flex gap-2">
                            <Input
                              placeholder="name"
                              value={input.name}
                              onChange={(e) => updateInput(index, { name: e.target.value })}
                              classNames={{ root: 'flex-1' }}
                            />
                            <Select
                              value={input.type}
                              onChange={(e) =>
                                updateInput(index, { type: e.target.value as SourceInput['type'] })
                              }
                              data={['string', 'url', 'text', 'number']}
                              classNames={{ root: 'w-28' }}
                            />
                            <Button
                              isIcon
                              variant="flat"
                              color="error"
                              size="sm"
                              onClick={() => removeInput(index)}
                            >
                              <Icon name="x" size="sm" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Steps */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-medium text-fg">Workflow Steps</label>
                      <div className="flex gap-1">
                        {(
                          ['discover', 'fetch', 'filter', 'select', 'extract', 'transform'] as const
                        ).map((type) => (
                          <Button
                            key={type}
                            size="sm"
                            variant="soft"
                            onClick={() => addStep(type)}
                            className="text-xs px-2"
                          >
                            +{type}
                          </Button>
                        ))}
                      </div>
                    </div>
                    {!source.steps || source.steps.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-dark-400 italic">
                        No custom steps. Workflow will be inferred from basic config.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {source.steps.map((step, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 p-2 rounded border border-gray-200 dark:border-dark-600"
                          >
                            <span className="text-xs font-mono bg-gray-100 dark:bg-dark-700 px-2 py-1 rounded">
                              {step.type}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-dark-400 flex-1 truncate">
                              {typeof step.config === 'string'
                                ? step.config || '(empty)'
                                : JSON.stringify(step.config)}
                            </span>
                            <Button
                              isIcon
                              variant="flat"
                              color="error"
                              size="sm"
                              onClick={() => removeStep(index)}
                            >
                              <Icon name="x" size="sm" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Preview */}
          <div className="space-y-6">
            {/* Inferred Workflow */}
            <Card skin="shadow" className="p-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                Inferred Workflow
              </h3>
              <div className="flex items-center gap-2 flex-wrap mb-4">
                {workflow.map((step, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div
                      className={clsx(
                        'px-3 py-1.5 rounded-lg text-sm font-medium text-white',
                        step === 'discover' && 'bg-amber-500',
                        step === 'fetch' && 'bg-blue-500',
                        step === 'filter' && 'bg-orange-500',
                        step === 'select' && 'bg-pink-500',
                        step === 'extract' && 'bg-purple-500',
                        step === 'transform' && 'bg-green-500'
                      )}
                    >
                      {step}
                    </div>
                    {index < workflow.length - 1 && (
                      <Icon name="arrow-right" size="sm" className="text-gray-400" />
                    )}
                  </div>
                ))}
                <Icon name="arrow-right" size="sm" className="text-gray-400" />
                <div className="px-3 py-1.5 rounded-lg bg-green-100 dark:bg-green-900/30 text-sm font-medium text-green-700 dark:text-green-300">
                  Persona
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-dark-400">
                Complexity level:{' '}
                <span className="font-medium text-gray-700 dark:text-dark-200">{complexity}</span>
              </p>
            </Card>

            <SourcePreview title="Source Definition (JSON)" data={source} format="json" />
            <SourcePreview title="User Config (YAML)" data={source} format="yaml" />
          </div>
        </div>
      </div>
    </div>
  );
}
